import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navItems = [
  { label: 'ホーム', path: '/' },
  { label: 'サービス', path: '/services' },
  { label: '制作実績', path: '/case-study' },
  { label: 'ブログ', path: '/blog' },
  { label: 'プロフィール', path: '/about' },
  { label: 'お問い合わせ', path: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    setScrolled(window.scrollY > 60);
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isTransparent = isHome && !scrolled;

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        isTransparent
          ? 'bg-transparent'
          : 'bg-white/95 backdrop-blur-md border-b border-[#E8F0FF]'
      }`}
      style={!isTransparent ? { boxShadow: '0 1px 20px rgba(30,111,255,0.08)' } : {}}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <div
            className="w-9 h-9 flex items-center justify-center rounded-full"
            style={{ background: 'linear-gradient(135deg, #1E6FFF 0%, #0099FF 100%)' }}
          >
            <span className="text-white font-bold text-sm tracking-wider">G</span>
          </div>
          <span
            translate="no"
            className={`font-bold text-xl tracking-widest transition-colors duration-500 ${
              isTransparent ? 'text-white' : 'text-[#0A1628]'
            }`}
          >
            GRAINN
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-10">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className="relative flex flex-col items-center gap-1.5 group whitespace-nowrap"
              >
                <span
                  className={`text-sm tracking-widest transition-all duration-300 ${
                    isTransparent
                      ? isActive
                        ? 'text-[#4DA3FF] font-semibold'
                        : 'text-white group-hover:text-white/70'
                      : isActive
                      ? 'text-[#1E6FFF] font-semibold'
                      : 'text-[#2C2C2C] group-hover:text-[#1E6FFF]'
                  }`}
                >
                  {item.label}
                </span>
                <span
                  className={`block h-1 rounded-full transition-all duration-300 ${
                    isActive
                      ? isTransparent
                        ? 'w-4 bg-[#4DA3FF]'
                        : 'w-4 bg-[#1E6FFF]'
                      : 'w-0 bg-transparent group-hover:w-2 ' +
                        (isTransparent ? 'group-hover:bg-white/40' : 'group-hover:bg-[#1E6FFF]/30')
                  }`}
                />
              </Link>
            );
          })}
          <Link
            to="/contact"
            className="text-white text-sm px-6 py-2.5 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer hover:opacity-90 hover:scale-105"
            style={{ background: 'linear-gradient(90deg, #1E6FFF 0%, #0099FF 100%)' }}
          >
            無料相談
          </Link>
        </nav>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <div className="w-8 h-8 flex items-center justify-center">
            <i
              className={`text-2xl ${isTransparent ? 'text-white' : 'text-[#0A1628]'} ${
                menuOpen ? 'ri-close-line' : 'ri-menu-line'
              }`}
            />
          </div>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-[#E8F0FF] px-6 py-6 flex flex-col gap-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm tracking-widest whitespace-nowrap transition-all ${
                  isActive
                    ? 'bg-[#E8F0FF] text-[#1E6FFF] font-semibold'
                    : 'text-[#2C2C2C] hover:bg-[#F5F8FF] hover:text-[#1E6FFF]'
                }`}
              >
                <span
                  className={`block w-1 h-4 rounded-full transition-all duration-300 ${
                    isActive ? 'bg-[#1E6FFF]' : 'bg-transparent'
                  }`}
                />
                {item.label}
                {isActive && (
                  <i className="ri-arrow-right-s-line ml-auto text-[#1E6FFF] text-base" />
                )}
              </Link>
            );
          })}
          <div className="pt-3 mt-1 border-t border-[#E8F0FF]">
            <Link
              to="/contact"
              className="block text-white text-sm px-6 py-3 rounded-full transition-colors whitespace-nowrap text-center hover:opacity-90"
              style={{ background: 'linear-gradient(90deg, #1E6FFF 0%, #0099FF 100%)' }}
            >
              無料相談
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
